import React from "react";
import { FieldError, UseFormRegisterReturn } from "react-hook-form";
import classNames from "classnames";
import styles from "./TextField.module.css";

type InputProps = React.InputHTMLAttributes<
  HTMLInputElement | HTMLTextAreaElement
>;

interface TextFieldProps {
  name: string;
  label: string;
  required?: boolean;
  type?: "text" | "email" | "tel" | "textarea";
  rows?: number;
  error?: FieldError;
  register: UseFormRegisterReturn;
  helpText?: string;
}

const TextField: React.FC<TextFieldProps> = ({
  name,
  label,
  required = false,
  type = "text",
  rows = 5,
  error,
  register,
  helpText,
}) => {
  const id = `${name}_${React.useId()}`;
  const isTextarea = type === "textarea";
  const ariaDescribedBy = classNames(
    error && `${id}-error`,
    helpText && `${id}-help`,
  );
  const classes = classNames(isTextarea ? styles.textarea : styles.input, {
    [styles.inputError]: error,
  });

  const Component = isTextarea ? "textarea" : "input";
  const inputProps: InputProps = {
    id,
    className: classes,
    ...register,
    "aria-describedby": ariaDescribedBy,
    ...(isTextarea ? { rows } : { type }),
  };

  return (
    <div className={styles.textfield}>
      <label htmlFor={id} className={styles.label}>
        {label} {required && "*"}
      </label>
      <Component {...inputProps} />
      {error && (
        <span id={`${id}-error`} className={styles.error} role="alert">
          {error.message}
        </span>
      )}
      {helpText && !error && (
        <span id={`${id}-help`} className={styles.helpText}>
          {helpText}
        </span>
      )}
    </div>
  );
};

export default TextField;
